import { formatDistanceToNow } from "date-fns";
import {
  QrCode,
  Clock,
  AlertCircle,
  Image,
  Utensils,
  Package,
  Check,
  Table,
} from "lucide-react";
import React, { useState } from "react";
import { Order, OrderStatus } from "@/store/orderStore";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge";
import { Button } from "@/components/ui/button";
import { CancelOrderDialog } from "@/components/orders/CancelOrderDialog";
import { useOrderStore } from "@/store/orderStore";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/lib/currencyFormatter";
import { useIsMobile } from "@/hooks/use-mobile";
import { PaymentMethodBadge } from "./PaymentMethodBadge";
import { updateOrderPaymentStatus } from '@/services/payments/paymentService';
import { updateOrderStatus as updateOrderStatusService } from '@/services/orders';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { OrderHeader } from "./OrderHeader";

interface OrderCardProps {
  order: Order;
  onClick: () => void;
  showPaymentStatus?: boolean;
}

export function OrderCard({
  order,
  onClick,
  showPaymentStatus = false,
}: OrderCardProps) {
  const { t } = useTranslation();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showConfirmPaymentDialog, setShowConfirmPaymentDialog] = useState(false);
  const { cancelOrder, updateOrder: updateOrderInStore } = useOrderStore();
  const isMobile = useIsMobile();

  const handleCancelOrder = async (orderId: string, reason: string) => {
    await cancelOrder(orderId, reason);
  };
  // Handler for updating order status
  const handleUpdateStatus = async (
    e: React.MouseEvent,
    status: OrderStatus
  ) => {
    e.stopPropagation();
    try {
      const result = await updateOrderStatusService(order.id, status, order.status);
      if (result.success) {
        // Update the order in the store
        updateOrderInStore({ ...order, status });
      } else {
        console.error("Error updating order status:", result.error);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  // Helper function to determine next status
  const getNextStatus = (currentStatus: OrderStatus): OrderStatus => {
    switch (currentStatus) {
      case "pending":
        return "preparing";
      case "preparing":
        return "ready";
      case "ready":
        return "delivered";
      case "delivered":
        return "completed";
      default:
        return currentStatus;
    }
  };

  const nextStatus = getNextStatus(order.status);
  const hasNextStatus =
    order.status !== "cancelled" && order.status !== "completed";

  // Get button text and icon based on next status
  const getNextStatusInfo = () => {
    switch (nextStatus) {
      case "preparing":
        return {
          text: t("startPreparing"),
          icon: <Utensils className="h-4 w-4 mr-1" />,
        };
      case "ready":
        return {
          text: t("markReady"),
          icon: <Package className="h-4 w-4 mr-1" />,
        };
      case "delivered":
        return {
          text: t("markDelivered"),
          icon: <Check className="h-4 w-4 mr-1" />,
        };
      case "completed":
        return {
          text: t("markCompleted"),
          icon: <Check className="h-4 w-4 mr-1" />,
        };
      default:
        return { text: "", icon: null };
    }
  };

  const { text: nextStatusText, icon: nextStatusIcon } = getNextStatusInfo();

  // Calculate total items quantity
  const totalQuantity = order.items.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const handleConfirmPayment = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmPaymentDialog(true);
  };
  const handleConfirmPaymentAction = async () => {
    await updateOrderPaymentStatus(order.id, true);
    updateOrderInStore({ ...order, is_payed: true });
    setShowConfirmPaymentDialog(false);
  };

  // Card background color logic
  const cardBgClass = order.payment_method === 'Cash' && !order.is_payed ? 'bg-orange-50' : '';

  return (
    <Card className={`overflow-hidden transition-all duration-200 hover:shadow-md text-xs sm:text-sm ${cardBgClass}`} onClick={onClick}>
      {/* Order Header with ID, Status and Actions */}
      {/* <CardHeader className="p-3 sm:p-4 text-xs sm:text-sm bg-gradient-to-r from-gray-50 to-gray-100 border-b">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 p-1.5 sm:p-2 rounded-full">
              <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base">
                {t("order")} #{order.id.slice(0, 8)}
              </h3>
              <div className="flex items-center text-xs sm:text-sm text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                {formatDistanceToNow(new Date(order.createdAt), {
                  addSuffix: true,
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-between sm:justify-between ">
            <div className="text-right w-full">
              <div className="font-bold text-base sm:text-lg">
                {formatCurrency(order.totalAmount)}
              </div>
              <div className="flex sm:flex-row justify-between gap-2 w-full">
                <Badge variant="outline" className="bg-gray-100 text-xs sm:text-sm">
                  {totalQuantity} {totalQuantity === 1 ? t("item") : t("items")}
                </Badge>
                {showPaymentStatus && order.payment_method === 'Cash' && !order.is_payed && (
                  <Button size="sm" variant="outline" className="text-orange-700 border-orange-300 text-xs sm:text-sm" onClick={handleConfirmPayment}>
                    {t('confirmPayment')}
                  </Button>
                )}
                {showPaymentStatus && order.payment_method === 'Cash' && order.is_payed && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                    {t('paid')}
                  </Badge>
                )}
                {showPaymentStatus && order.payment_method !== 'Cash' && (
                  <Badge
                    variant="outline"
                    className={
                      order.is_payed
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                    }
                  >
                    {order.is_payed ? t('paid') : t('unpaid')}
                  </Badge>
                )}
                {order.payment_method && <PaymentMethodBadge method={order.payment_method} />}
              </div>
            </div>
          </div>
        </div>
      </CardHeader> */}
      <OrderHeader order={order} showPaymentStatus={showPaymentStatus} handleConfirmPayment={handleConfirmPayment } />

      <CardContent className="p-0">
        {/* Table Info and Status Section */}
        <div className="p-3 sm:p-4 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center">
            <Table className="h-4 w-4 mr-2 text-gray-500" />
            <span className="font-medium text-sm sm:text-base">
              {t("table")} {order.tableNumber} - {order.tableName}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-0">
            <OrderStatusBadge status={order.status} />

            {/* Status Update Button */}
            {hasNextStatus && (
              <Button
                size="sm"
                variant="outline"
                className="bg-primary/10 hover:bg-primary/20 border-primary/20 text-xs sm:text-sm h-8 px-2 sm:px-3"
                onClick={(e) => handleUpdateStatus(e, nextStatus)}
              >
                {nextStatusIcon}
                <span className="hidden sm:inline">{nextStatusText}</span>
                <span className="sm:hidden">{t(nextStatus)}</span>
              </Button>
            )}

            {/* Cancel Button */}
            {order.status !== "cancelled" && order.status !== "completed" && (
              <Button
                variant="destructive"
                size="sm"
                className="h-8 text-xs sm:text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCancelDialog(true);
                }}
              >
                {t("cancelOrder")}
              </Button>
            )}
          </div>
        </div>

        {/* Special Instructions Section (if any) */}
        {order.specialInstructions && (
          <div className="p-3 sm:p-4 bg-amber-50 border-y border-amber-100">
            <div className="flex items-center gap-1.5 mb-1 text-amber-800">
              <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <h4 className="font-medium text-sm sm:text-base">
                {t("specialInstructions")}
              </h4>
            </div>
            <p className="text-xs sm:text-sm whitespace-pre-wrap text-gray-700">
              {order.specialInstructions}
            </p>
          </div>
        )}

        {/* Order Items Section */}
        <div className="p-3 sm:p-4">
          <div className="space-y-3">
            {order.items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 sm:gap-3">
                {/* Item Image */}
                <div className="h-12 w-12 sm:h-16 sm:w-16 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.menu_item_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-gray-400">
                      <Image className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                  )}
                </div>

                {/* Item Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h4 className="font-medium text-sm sm:text-base truncate mr-2">
                      {item.menu_item_name}
                    </h4>
                    <span className="font-medium text-sm sm:text-base whitespace-nowrap">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                  <div className="flex items-center text-xs sm:text-sm text-muted-foreground mt-0.5">
                    <span>{item.quantity}x</span>
                    <span className="mx-2">•</span>
                    <span>{formatCurrency(item.price)}</span>
                  </div>
                  {item.notes && (
                    <div className="mt-1 text-xs bg-gray-50 p-1 rounded text-gray-600 line-clamp-2">
                      {item.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Section with Order Info */}
        <div className="p-3 sm:p-4 bg-gray-50 border-t">
          <div className="flex items-center justify-between text-xs sm:text-sm">
            <div className="text-muted-foreground">
              {new Date(order.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              • {new Date(order.createdAt).toLocaleDateString()}
            </div>
            <Badge variant="outline" className="text-xs">
              ID: {order.id.slice(-6)}
            </Badge>
          </div>
        </div>
      </CardContent>

      <CancelOrderDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        orderId={order.id}
        onCancel={handleCancelOrder}
      />

      <Dialog open={showConfirmPaymentDialog} onOpenChange={setShowConfirmPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('confirmPayment')}</DialogTitle>
          </DialogHeader>
          <div>{t('areYouSureCollectedCash')}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmPaymentDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleConfirmPaymentAction}>{t('confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
